import React, { FC, useContext, useEffect, useState } from 'react'

// Libraries
import Select from 'react-select'

// Components
import MainModal from './common/MainModal'
import { AddTeamMemberToPaperworkHeader } from './components/AddTeamMemberToPaperworkHeader'

import FirestoreService from '../../services/firestoreService'
import RiderTeamMemberService from '../../services/firebase-service/rider-team-member/RiderTeamMemberService'
import UserService from '../../services/firebase-service/users/userService'

// Helpers
import { getUserFullName } from '../../helpers/helpers'
import { calculateAge, getConvertedData } from '../../models/interface.helper'
import { generatePaperworkDocument } from '../../helpers/sign'
import useToasterHelper from '../../helpers/ToasterHelper'
import customImageComponent from '../common/CustomImageComponent'
import { getFilteredPaperwork } from '../../helpers/documents'

// Store
import { setRidersTeamMembersUpdates } from '../../store/paperworks/paperworkSlice'
import { riderActions } from '../../store/rider/riderSlice'
import { useAppDispatch } from '../../store/hooks'

import useTeamMembers from '../../hooks/useTeamMembers'

// Models
import { RiderTeamMemberModel } from '../../models/rider-team-member/riderTeamMember.model'
import { EventDetailsModel } from '../../models/event-details/event-details.model'
import { EventReviewPublishModel } from '../../models/event-review-publish/event-review-publish.model'

// Interfaces
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IUserInterface } from '../../models/users/user.interface'
import { IRiderTeamMemberInterface } from '../../models/rider-team-member/riderTeamMember.interface'

// Styles
import { customStyles } from '../customStyles/ReactSelectCustomStyle'

// Constants
import { CONST } from '../../const/const'
import { MODAL_CONSTS } from '../../const/modal-const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { HandleModalContext } from '../../layout/mainlayout/MainLayout'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

type IProps = {
  dataToPassOn: {
    teamMembers: IUserInterface[]
    riderTeamMember: IRiderTeamMemberInterface
    rider: IRegistrationByDayInterface
    defaultRole?: string
    onlyPaperwork?: boolean
  }
}
const TEAM_MEMBER_ROLES = CONST.UI.TEAM_MEMBERS_ROLES

interface ISelectedTeamMember extends IUserInterface {
  role: {
    label: string
    value: string
  } | null
}
export const AddTeamMemberToPaperworkStep2: FC<IProps> = ({ dataToPassOn }) => {
  const handleModalContext = useContext(HandleModalContext)

  const dispatch = useAppDispatch()
  const { saveRidersTeamMembers } = useTeamMembers()

  const { teamMembers, rider, defaultRole, onlyPaperwork } = dataToPassOn

  const ROLES = [
    ...Object.values(TEAM_MEMBER_ROLES)
      .filter((role) => role !== TEAM_MEMBER_ROLES.RIDER)
      .map((value) => ({
        value,
        label: value,
      })),
    { label: 'Rider', value: 'Rider' },
  ]
  const [loading, setLoading] = useState(false)
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<ISelectedTeamMember[]>([])
  const [documentsCount, setDocumentsCount] = useState(0)
  const [documentsLoading, setDocumentsLoading] = useState(1)

  const toastFunction = useToasterHelper()

  useEffect(() => {
    if (teamMembers) {
      const updatedTeamMembers: ISelectedTeamMember[] = teamMembers.map((member) => ({
        ...member,
        role: defaultRole ? { label: defaultRole, value: defaultRole } : null,
      }))
      setSelectedTeamMembers(updatedTeamMembers)
    }
  }, [defaultRole, teamMembers])

  const onSubmit = async () => {
    let teamMemberToAdd: IRiderTeamMemberInterface | null = null
    let teamMemberToUpdate: IRiderTeamMemberInterface | null = null

    const doesNotHaveRole = selectedTeamMembers.some((user) => !user.role)

    if (doesNotHaveRole) {
      return toastFunction.error({ message: 'All selected team members should have a role' })
    }
    if (!rider?.userId) {
      toastFunction.error({ message: 'User id not found' })
      return
    }

    try {
      setLoading(true)

      for (let currentUser of selectedTeamMembers) {
        let memberAge = calculateAge(currentUser?.userDOB)

        if (memberAge < 18) {
          return toastFunction.error({ message: 'Member is minor' })
        }

        if (!currentUser.role?.value) {
          return toastFunction.error({ message: 'Role is required' })
        }

        if (!rider.riderId || !rider.horseId || !rider.registrationDocId) {
          toastFunction.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
          return null
        }

        const teamMemberInDb = await RiderTeamMemberService.getRiderTeamMembers({
          teamMemberId: currentUser.id,
          role: currentUser.role?.value,
          riderId: rider.riderId,
          horseId: rider.horseId,
          registrationDocId: rider.registrationDocId,
        })

        // If the documents are not generated, we need to generate documents for the existing member
        if (teamMemberInDb?.[0]) {
          teamMemberToUpdate = teamMemberInDb[0]
        } else {
          teamMemberToAdd = new RiderTeamMemberModel({
            mailLog: [],
            userId: rider.userId,

            eventId: rider.eventId || '',
            eventName: rider.eventName || '',

            riderDob: rider.riderDob,
            riderId: rider.riderId,
            riderName: rider.riderName,
            riderEmail: rider.riderEmail,
            riderNameNGram: rider.riderNameNGram,
            riderCountry: rider.riderCountry,
            riderProfilePicture: rider.riderProfilePicture,

            registrationDocId: rider.registrationDocId,

            teamMemberId: currentUser.id,
            teamMemberDob: currentUser.userDOB,
            teamMemberName: getUserFullName(currentUser),
            teamMemberRole: currentUser.role?.value,
            teamMemberEmail: currentUser.userEmail,
            teamMemberProfilePicture: currentUser.userProfilePicture,

            horseId: rider.horseId,
            horseName: rider.horseName,
            horseProfilePicture: rider.horseProfilePicture || null,

            modified: new Date().toString(),
            created: new Date().toString(),
          }).toObject()
        }

        const publishedEventInDbSnapShot = await FirestoreService.getItem(
          COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
          rider.eventId!
        )

        const publishedEvent = getConvertedData(
          EventReviewPublishModel.fromFirestoreDoc(publishedEventInDbSnapShot).toObject()
        )

        const filteredPaperworkDocuments = getFilteredPaperwork(
          publishedEvent?.EventPaperwork ?? null
        )

        setDocumentsCount(filteredPaperworkDocuments?.length ?? 0)

        const updatedTeamMembers = await saveRidersTeamMembers({
          ridersTeamMembersToAdd: teamMemberToAdd ? [teamMemberToAdd] : [],
          ridersTeamMembersToUpdate: teamMemberToUpdate ? [teamMemberToUpdate] : [],
        })

        const eventDetailsSnaps = await FirestoreService.getItem(
          COLLECTIONS.EVENT_DETAILS.NAME,
          rider.eventId!
        )

        const eventDetails = EventDetailsModel.fromFirestoreDoc(eventDetailsSnaps).toObject()

        const userInDb = await UserService.getUserById(rider.userId)

        for (const document of filteredPaperworkDocuments) {
          for (const teamMember of [
            ...(updatedTeamMembers?.ridersTeamMembersToAdd ?? []),
            ...(updatedTeamMembers?.ridersTeamMembersToUpdate ?? []),
          ]) {
            await generatePaperworkDocument({
              paperwork: document,
              currRidersTeamMember: teamMember,
              userData: userInDb,
              eventId: rider.eventId!,
              EventDetails: eventDetails,
              dateOfSignature: new Date(),
              isSigned: false,
            })
            setDocumentsLoading((prev) => prev + 1)
          }
        }
      }

      dispatch(setRidersTeamMembersUpdates())
      dispatch(riderActions.setIsAppPaperworkSign(false))
      handleModalContext?.handleModal(false, MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_2)
    } catch (error) {
      console.error('error', error)
      toastFunction.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  return (
    <MainModal
      title={onlyPaperwork ? 'Add paperwork' : 'Add role to entry'}
      show={true}
      type="ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_2"
      size="md"
      titleClassName="!font-normal"
      className="z-[50]"
      overlayClassName="z-[49]"
      buttons={[
        {
          disabled: loading,
          label: loading
            ? `Please wait... ${documentsLoading - 1 || 0} of ${documentsCount || '...'} ready`
            : 'Assign & Send Paperwork',
          bgClass: 'bg-SeabiscuitMainThemeColor !w-full',
          textClass: 'text-white',
          onClick: onSubmit,
        },
        {
          loading,
          disabled: loading,
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor !w-full',
          className: 'outline-none',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          onClick: () =>
            handleModalContext?.handleModal(
              false,
              MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_2
            ),
        },
      ]}
    >
      <div className="text-SeabiscuitDark200ThemeColor">
        <AddTeamMemberToPaperworkHeader registrationByDay={rider} />
        <h2 className="font-semibold text-[18px]">Assign roles</h2>
        <p>Assign roles to new team members to send paperwork to sign</p>
        <ul className="flex flex-col gap-5 mt-4">
          {selectedTeamMembers.map((user, index) => (
            <li key={user.id + '' + index} className={'flex gap-3 items-center'}>
              <div className={'flex flex-1 gap-2 items-center'}>
                {customImageComponent(
                  user.userProfilePicture,
                  getUserFullName(user),
                  'w-[45px] h-[45px] avatarImg rounded object-cover border'
                )}
                <div>
                  <h4 className={'text-[#122B46] font-normal text-[16px]'}>
                    {getUserFullName(user)}
                  </h4>
                  <p className={'text-[#122B4680] text-[14px]'}>{user.userEmail}</p>
                </div>
              </div>
              <div>
                <Select
                  isDisabled={onlyPaperwork || false}
                  isMulti={false}
                  onChange={(select) => {
                    const newUsers = [...selectedTeamMembers]
                    if (newUsers[index]) newUsers[index].role = select as (typeof ROLES)[0]
                    setSelectedTeamMembers(newUsers)
                  }}
                  value={user.role}
                  // onBlur={onBlur}
                  placeholder={<div className="flex text-[13px]">Role</div>}
                  options={ROLES}
                  className={`border rounded-md w-40 !border-[#D3DAEE]`}
                  isClearable={false}
                  styles={customStyles}
                  isSearchable={true}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </MainModal>
  )
}
